import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Megaphone } from "lucide-react";

const AnnouncementManager = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newAnnouncement, setNewAnnouncement] = useState({
    venueId: "",
    title: "",
    message: "",
    type: "general",
    expiresAt: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [announcementsRes, venuesRes] = await Promise.all([
        axios.get(`${API}/venue-owner/announcements`),
        axios.get(`${API}/venues`)
      ]);
      setAnnouncements(announcementsRes.data);
      setVenues(venuesRes.data.filter(v => v.ownerId === user.id));
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAnnouncement.venueId || !newAnnouncement.title || !newAnnouncement.message) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/announcements`, newAnnouncement);
      toast.success("Duyuru oluşturuldu!");
      setShowCreate(false);
      setNewAnnouncement({
        venueId: "",
        title: "",
        message: "",
        type: "general",
        expiresAt: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Duyuru oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Duyuruyu silmek istediğinize emin misiniz?")) return;

    try {
      await axios.delete(`${API}/announcements/${id}`);
      toast.success("Duyuru silindi");
      fetchData();
    } catch (error) {
      toast.error("Duyuru silinemedi");
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      discount: "İndirim",
      tournament: "Turnuva",
      maintenance: "Bakım",
      general: "Genel"
    };
    return labels[type] || type;
  };

  const getTypeBadge = (type) => {
    const colors = {
      discount: "bg-green-100 text-green-800",
      tournament: "bg-purple-100 text-purple-800",
      maintenance: "bg-orange-100 text-orange-800",
      general: "bg-blue-100 text-blue-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="outline" 
            onClick={() => navigate("/venue-owner-dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Panele Dön
          </Button>

          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Duyuru Yönetimi</h1>
            <Button 
              onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Duyuru
            </Button>
          </div>

          {/* Announcements List */}
          {announcements.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center text-gray-500">
                  <Megaphone className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Henüz duyuru yok</p>
                  <p className="text-sm mt-2">İlk duyurunuzu oluşturun</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {announcements.map((ann) => (
                <Card key={ann.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold">{ann.title}</h3>
                          <Badge className={getTypeBadge(ann.type)}>
                            {getTypeLabel(ann.type)}
                          </Badge>
                          {!ann.isActive && (
                            <Badge variant="outline">Pasif</Badge>
                          )}
                        </div>
                        <p className="text-gray-700 mb-2">{ann.message}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>{ann.venueName}</span>
                          <span>•</span>
                          <span>{new Date(ann.createdAt).toLocaleDateString('tr-TR')}</span>
                          {ann.expiresAt && (
                            <>
                              <span>•</span>
                              <span>Bitiş: {new Date(ann.expiresAt).toLocaleDateString('tr-TR')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(ann.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Yeni Duyuru Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Saha</Label>
              <Select
                value={newAnnouncement.venueId}
                onValueChange={(val) => setNewAnnouncement({...newAnnouncement, venueId: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Saha seçin" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map(venue => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Duyuru Tipi</Label>
              <Select
                value={newAnnouncement.type}
                onValueChange={(val) => setNewAnnouncement({...newAnnouncement, type: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Genel</SelectItem>
                  <SelectItem value="discount">İndirim</SelectItem>
                  <SelectItem value="tournament">Turnuva</SelectItem>
                  <SelectItem value="maintenance">Bakım</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Başlık</Label>
              <Input
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                placeholder="Örn: %20 İndirim Kampanyası"
              />
            </div>

            <div>
              <Label>Mesaj</Label>
              <Textarea
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
                placeholder="Duyuru detayları..."
                rows={4}
              />
            </div>

            <div>
              <Label>Son Geçerlilik Tarihi (Opsiyonel)</Label>
              <Input
                type="date"
                value={newAnnouncement.expiresAt}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, expiresAt: e.target.value})}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowCreate(false)}
                className="flex-1"
              >
                İptal
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600"
              >
                {creating ? "Oluşturuluyor..." : "Duyuru Oluştur"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnouncementManager;
